import type { ServiceRequest } from '@medplum/fhirtypes';
import React, { useState } from 'react';

import {
  Button,
  Icon,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Stack,
  Tag,
  Text,
  useToast,
} from '@eluve/components';
import { useFhirRestRead } from '@eluve/fhir-data';
import { FhirVersionHistory, formatFhirDate } from '@eluve/fhir-renderer';
import { useMedplum } from '@eluve/medplum-client';

import {
  type TodoReviewDecision,
  reviewServiceRequest,
} from '../data/service-requests';
import { isPendingReview } from '../review-markers';
import { todoDueDate } from '../todo-list';

/**
 * Deep-linkable side drawer for one patient to-do (ENG-5373 for
 * service requests): the to-do's fields, Accept/Dismiss for a pending AI
 * proposal (the same tag transition the backend care-plan review applies),
 * and the `_history` audit trail (REST — the FHIR GraphQL spec has no
 * `_history`).
 */
export type TodoDetailDrawerProps = {
  todo: ServiceRequest | undefined;
  open: boolean;
  onClose: () => void;
  /** Called after a review decision lands so the owner can refetch reads. */
  onReviewed: () => void;
};

const FieldRow: React.FC<{ label: string; value: string | null }> = ({
  label,
  value,
}) => (
  <div className="flex items-baseline justify-between gap-4 py-1.5">
    <Text inline size="sm" color="tertiary" className="shrink-0">
      {label}
    </Text>
    <Text inline size="sm" className="text-right privacy-text">
      {value ?? '—'}
    </Text>
  </div>
);

export const TodoDetailDrawer: React.FC<TodoDetailDrawerProps> = ({
  todo,
  open,
  onClose,
  onReviewed,
}) => {
  const { client } = useMedplum();
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  const todoId = todo?.id;
  const todoVersion = todo?.meta?.versionId;
  const history = useFhirRestRead(
    todoId ? (medplum) => medplum.readHistory('ServiceRequest', todoId) : null,
    [todoId, todoVersion],
  );
  const versions: ServiceRequest[] = (history.data?.entry ?? []).flatMap(
    (entry) => (entry.resource ? [entry.resource] : []),
  );

  const decide = async (decision: TodoReviewDecision) => {
    if (!client || !todoId) return;
    setBusy(true);
    try {
      await reviewServiceRequest(client, {
        serviceRequestId: todoId,
        decision,
      });
      toast.success(
        decision === 'dismiss' ? 'Proposal dismissed.' : 'To-do verified.',
      );
      onReviewed();
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : 'Could not save the review decision.',
      );
    } finally {
      setBusy(false);
    }
  };

  const done = todo?.status === 'completed';
  const pending = todo ? isPendingReview(todo) : false;
  const dueDate = todo ? todoDueDate(todo) : null;

  return (
    <Sheet open={open} onOpenChange={(next) => !next && onClose()}>
      <SheetContent
        side="right"
        className="w-[min(640px,94vw)] gap-0 overflow-y-auto sm:max-w-none"
        data-testid="todo-detail-drawer"
      >
        {todo && (
          <>
            <SheetHeader>
              <SheetTitle className="privacy-text">
                {todo.code?.text ?? '(unnamed to-do)'}
              </SheetTitle>
              <SheetDescription className="privacy-text">
                {[
                  done ? 'Done' : 'Active',
                  dueDate ? `Due ${formatFhirDate(dueDate)}` : undefined,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </SheetDescription>
            </SheetHeader>

            <Stack gap={6} align="stretch" className="px-4 pb-6">
              {pending && (
                <div
                  data-testid="todo-proposal-card"
                  className="rounded-lg border border-border-insights-subtle bg-bg-insights-subtle p-4"
                >
                  <Stack
                    direction="horizontal"
                    gap={2}
                    align="center"
                    fullWidth={false}
                  >
                    <Icon
                      name="Sparkles"
                      size="xs"
                      className="text-text-insights"
                    />
                    <Text
                      inline
                      size="sm"
                      weight="semibold"
                      className="text-text-insights"
                    >
                      Eluve proposed this to-do
                    </Text>
                  </Stack>
                  <Text
                    size="xs"
                    color="tertiary"
                    weight="regular"
                    className="mt-1"
                  >
                    Accepting keeps it on the patient's list; dismissing hides
                    it.
                  </Text>
                  <Stack
                    direction="horizontal"
                    gap={2}
                    align="center"
                    className="mt-3"
                  >
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={busy || !client}
                      onClick={() => void decide('dismiss')}
                      data-testid="todo-proposal-dismiss"
                    >
                      Dismiss
                    </Button>
                    <div className="flex-1" />
                    <Button
                      size="sm"
                      disabled={busy || !client}
                      onClick={() => void decide('accept')}
                      data-testid="todo-proposal-accept"
                    >
                      Accept
                    </Button>
                  </Stack>
                </div>
              )}

              <div>
                <FieldRow label="Status" value={done ? 'Done' : 'Active'} />
                {pending && (
                  <div className="flex items-baseline justify-between gap-4 py-1.5">
                    <Text
                      inline
                      size="sm"
                      color="tertiary"
                      className="shrink-0"
                    >
                      Review
                    </Text>
                    <Tag
                      pill
                      subtle
                      size="xs"
                      color="insights"
                      label="Unverified"
                    />
                  </div>
                )}
                <FieldRow
                  label="Requested by"
                  value={todo.requester?.display ?? null}
                />
                <FieldRow
                  label="Source"
                  value={todo.basedOn?.length ? 'Care plan' : null}
                />
                <FieldRow
                  label="Due date"
                  value={dueDate ? (formatFhirDate(dueDate) ?? null) : null}
                />
                <FieldRow label="Description" value={todo.code?.text ?? null} />
              </div>

              <div>
                <Text
                  size="xs"
                  color="tertiary"
                  weight="medium"
                  className="mb-2 tracking-wide uppercase"
                >
                  Change log & audit trail
                </Text>
                {history.loading ? (
                  <Text size="sm" color="tertiary" weight="regular">
                    Loading history…
                  </Text>
                ) : (
                  <FhirVersionHistory versions={versions} />
                )}
              </div>
            </Stack>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
